import axios, { AxiosError, AxiosInstance } from 'axios';
import { getStoredAccessToken } from '@auth/token';

/**
 * Axios `baseURL` from `import.meta.env.API_BASE_URL` only (set in `.env` / build env).
 * No runtime override and no same-origin fallback.
 */
function resolveApiBaseUrl(): string {
  const raw = import.meta.env.API_BASE_URL;
  if (typeof raw !== 'string' || raw.trim() === '') {
    throw new Error(
      'Missing API_BASE_URL. Add it to frontend `.env` (see `.env.example`). There is no default.',
    );
  }
  return normalizeApiBaseUrl(raw);
}

function normalizeApiBaseUrl(raw: string): string {
  const t = raw.trim();
  if (t === '/' || t === '') {
    throw new Error('API_BASE_URL must be a full API origin (relative "/" is not allowed).');
  }
  return t.replace(/\/+$/, '');
}

/** Normalized origin from `API_BASE_URL` (no trailing slash). Used for HLS and any absolute media URLs. */
export const resolvedApiBaseUrl = resolveApiBaseUrl();

export const http: AxiosInstance = axios.create({
  baseURL: resolvedApiBaseUrl,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

let accessTokenProvider: (() => string | null) | null = getStoredAccessToken;
let unauthorizedHandler: (() => void) | null = null;

export function registerAuthTokenProvider(provider: (() => string | null) | null): void {
  accessTokenProvider = provider;
}

export function registerUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

function browserIanaTimeZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof tz === 'string' && tz.length > 0 && tz.length <= 64) return tz;
  } catch {
    /* ignore */
  }
  return undefined;
}

http.interceptors.request.use((config) => {
  const token = accessTokenProvider?.();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  const tz = browserIanaTimeZone();
  if (tz) {
    config.headers.set('X-Client-Timezone', tz);
  }
  return config;
});

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class HttpError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

http.interceptors.response.use(
  (res) => res,
  async (err: AxiosError<{ error?: ApiError }>) => {
    if (axios.isCancel(err)) return Promise.reject(err);
    const status = err.response?.status ?? 0;
    const raw = err.response?.data;
    let apiErr = typeof raw === 'object' && raw && !(raw instanceof Blob) ? (raw as { error?: ApiError }).error : undefined;

    if (raw instanceof Blob) {
      try {
        const text = await raw.text();
        const parsed = JSON.parse(text) as { error?: ApiError };
        apiErr = parsed?.error;
      } catch {
        /* ignore */
      }
    }

    if (status === 401) {
      unauthorizedHandler?.();
    }

    throw new HttpError(
      apiErr?.message ?? err.message ?? 'Request failed',
      status,
      apiErr?.code ?? 'NETWORK_ERROR',
      apiErr?.details,
    );
  },
);
